import { useQuery } from '@apollo/client';
import { useAuthState } from '@auth';
import { useCurrentCommunityId } from '@communities/community/hooks/useCurrentCommunityId';
import { transformErrors } from '@core/adapters/errors';
import { UserRoles, type DomainError } from '@core/domain';
import type { GetCommunityForModerationQuery } from '@core/graphql/types';
import { useMemo } from 'react';

import { GET_COMMUNITY_FOR_MODERATION } from '../graphql';

interface UseCommunityDetailForModerationOptions {
  /** Polling interval in milliseconds. When set, the query will automatically refetch at this interval. */
  pollInterval?: number;
}

/**
 * Hook for fetching community details specifically for moderation pages
 *
 * Uses GET_COMMUNITY_FOR_MODERATION query which fetches only identity, display,
 * and moderationStats fields. This is significantly lighter than the full
 * GET_COMMUNITY query, making it suitable for 30-second polling.
 */
export function useCommunityDetailForModeration(options?: UseCommunityDetailForModerationOptions) {
  const communityId = useCurrentCommunityId();
  const { hasRoleAccess } = useAuthState();

  const { data, loading, error, refetch } = useQuery<GetCommunityForModerationQuery>(GET_COMMUNITY_FOR_MODERATION, {
    variables: { id: communityId },
    skip: !communityId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    ...(options?.pollInterval && { pollInterval: options.pollInterval }),
  });

  const community = data?.community || null;

  const isCommunityAdmin = useMemo(() => {
    if (!community) return false;

    return hasRoleAccess(UserRoles.ADMIN, community.communityGroupId, community.id);
  }, [community, hasRoleAccess]);

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  return {
    community,
    isCommunityAdmin,
    loading,
    errors,
    refetch,
  };
}
